import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { FormBuilder, Validators } from '@angular/forms'
import { catchError, firstValueFrom, throwError } from 'rxjs'
import { Player } from '../../core/models/player.model'
import { MatSnackBar } from '@angular/material/snack-bar'
import { environment } from '../../../environments/environment'
import { DomSanitizer, SafeUrl } from '@angular/platform-browser'

@Injectable({
  providedIn: 'root'
})
export class PlayerService {
  constructor(
    private _snackBar: MatSnackBar, 
    private sanitizer: DomSanitizer,
    private fb:FormBuilder, 
    private http: HttpClient
  ) { }

  readonly baseURL = environment.DOMAIN_URL+'/api/players/'
  list:Player[] = []

  playerAtletiForm = this.fb.group({
    player: this.fb.control<Player | null>(null),
    season: ['2024/2025',Validators.required],
    homegrown: ['No']
  })

  playerForm = this.fb.group({
    player: this.fb.control<Player | null>(null),
    player_name: [''],
    sub_name: [''],
    sub_player: this.fb.control<Player | null>(null),
    minute: [0],
  })

  playerSearchForm = this.fb.group({
    name: [''],
    commonName: [''],
    imgUrl: [''],
    nation: [''],
    age: this.fb.control<Date | null>(null),
    commonPosition: [''],
    gender: ['male', Validators.required]
  })

  fetchPlayerList(){
    return this.http.get(this.baseURL)
    .pipe(catchError(this.errorHandler))
  }

  fetchSeasonPlayerList(season:string, atm?:boolean){
    let params = new HttpParams().set('season', season)
    if(atm) params.set('atm', atm)
    return this.http.get(this.baseURL+'season', {params})
    .pipe(catchError(this.errorHandler))
  }
  fetchMatchPlayerList(match:string, atm?:boolean){
    let params = new HttpParams().set('match', match)
    if(atm) params.set('atm', atm)
    return this.http.get(this.baseURL+'match', {params})
    .pipe(catchError(this.errorHandler))
  }
  fetchPlayers(inputValue:string, type:string){
    let params = new HttpParams().set('name', inputValue).set('queryType', type)
    return this.http.get(this.baseURL, {params})
    .pipe(catchError(this.errorHandler))
  }

  fetchPlayerSquad(season:string, team:string){
    return this.http.post(this.baseURL+'squad', {season, team})
    .pipe(catchError(this.errorHandler))
  }
  initMarket(){
    return this.http.get(this.baseURL+'market')
    .pipe(catchError(this.errorHandler))
  }

  getPlayer(player_id:string){
    return this.http.get(this.baseURL+player_id)
    .pipe(catchError(this.errorHandler))
  }

  postPlayerToTeam(team:string){
    return this.http.post(this.baseURL+'team', {player: this.playerAtletiForm.value, team})
    .pipe(catchError(this.errorHandler))
  }

  putPlayerToTeam(team:string){
    return this.http.put(this.baseURL+'team/'+this.playerAtletiForm.get('_id')?.value, {player: this.playerAtletiForm.value, team})
    .pipe(catchError(this.errorHandler))
  }

  createPlayer(){
    return this.http.post(this.baseURL, this.playerSearchForm.value)
    .pipe(catchError(this.errorHandler))
  }
  updatePlayer(id:string){
    return this.http.put(this.baseURL+id, this.playerSearchForm.value)
    .pipe(catchError(this.errorHandler))
  }

  deletePlayerFromTeam(_id:string, team:string){
    return this.http.delete(this.baseURL+'team/'+_id)
    .pipe(catchError(this.errorHandler))
  }

  fetchSearchResults(query: string) {
    return this.http.get(this.baseURL + 'search', {
      params: { query }
    })
    .pipe(catchError(this.errorHandler))
  }

  private errorHandler(error: HttpErrorResponse) {
    if (error.status === 0) console.error('An error occurred:', error.error)
    else console.error(`Backend returned code ${error.status}, body was: `, error.error)
    return throwError(() => new Error('Something bad happened; please try again later.'))
  }

  async fetchFoto(_id:string) {
    try{
      const response = await firstValueFrom(this.http.get<{ foto: string }>(this.baseURL+'foto/'+_id)
      .pipe(catchError(this.errorHandler)))
      return this.getSanitizedImageUrl(response.foto) as SafeUrl
    } catch (error) {
      return undefined
    }
  }
  
  getSanitizedImageUrl(base64: string): SafeUrl {
    const url = `data:image/jpeg;base64,${base64}`
    return this.sanitizer.bypassSecurityTrustResourceUrl(url)
  }
}
